import { accountCodeError, normalizeAccountCode } from './accountCode.js'
import { toCents } from './budgetMath.js'
import { UnreadableSpreadsheet } from './spreadsheet.js'
import type { Category } from '../schema/common.js'

/**
 * Importing budget codes from a building's budget workbook.
 *
 * Written against the district's 2026-27 Middle School Budget, which is the
 * shape these files actually take: three title rows and a blank above the
 * headings, a `Line` column whose numbers repeat, the budget column named for
 * its year ("2026/27 Proposed Budget"), and a notes column marked for district
 * administration only.
 *
 * Three rules follow from that file and are worth keeping:
 *
 *   - **The header row is found, not assumed.** Row 1 is the district's name.
 *   - **Columns are recognised by what their heading means**, so next year's
 *     "2027/28 Proposed Budget" still reads. Only the three columns this needs
 *     are ever read — never a notes column, whatever it holds.
 *   - **A line is an account code.** The `Line` numbers are not unique and are
 *     ignored; a row with no code is a heading, a subtotal or spacing.
 */

/** One budget line that can be imported. */
export interface BudgetImportLine {
  /** Canonical hyphenated form. */
  accountCode: string
  /** The file's description, tidied of doubled spaces. Becomes the display name. */
  description: string
  /** Dollars, rounded to the cent. Never negative. */
  amount: number
  /** A label of at most 24 characters, derived from the description. */
  suggestedShortLabel: string
  /** True when the description did not fit as it was and was shortened. */
  shortLabelShortened: boolean
  /** A best guess from the description's wording. Reference only in the app. */
  category: Category
}

/** A row that looks like a budget line but cannot be imported, and why. */
export interface BudgetImportProblem {
  /** Enough of the row for a person to find it in the file. */
  row: string
  reason: string
}

export interface ParsedBudget {
  lines: BudgetImportLine[]
  problems: BudgetImportProblem[]
  /** The headings that were read, so the preview can say which column was used. */
  columns: { accountCode: string; description: string; amount: string }
  /** Title rows above the headings, e.g. "Middle School Budget". */
  titles: string[]
  /** The fiscal year the file names, by its ending year (2026-27 → 2027), if any. */
  namedFiscalYear: number | null
}

export const SHORT_LABEL_MAX = 24

/** How far down to look for the heading row before giving up. */
const HEADER_SEARCH_ROWS = 30

const DISPLAY_NAME_MAX = 120

// ─────────────────────────────────────────────────────────────────────────────
// Reading the grid
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Turn a sheet's raw rows into importable budget lines.
 *
 * Throws `UnreadableSpreadsheet` with a message naming what was looked for
 * when no heading row can be found, rather than guessing at columns — a guess
 * that read the notes column as descriptions would look entirely plausible.
 *
 * `targetYear` is the fiscal year the import is for, by its ending year. Given
 * one, a money column whose heading names that year wins over one naming
 * another, however the two are worded — see `bestAmountColumn`.
 */
export function parseBudgetGrid(
  grid: readonly (readonly (string | undefined)[])[],
  targetYear?: number,
): ParsedBudget {
  const header = findHeader(grid, targetYear)
  if (header === null) {
    throw new UnreadableSpreadsheet(
      'No heading row was found. The sheet needs columns headed "Account Code", ' +
        '"Description" and a budget amount (for example "2026/27 Proposed Budget").',
    )
  }

  const { rowIndex, codeColumn, descriptionColumn, amountColumn, headings } = header

  const titles = grid
    .slice(0, rowIndex)
    .flatMap((row) => Array.from(row, (cell) => clean(cell ?? '')))
    .filter((text) => text !== '')

  const lines: BudgetImportLine[] = []
  const problems: BudgetImportProblem[] = []
  const seen = new Set<string>()

  for (const row of grid.slice(rowIndex + 1)) {
    const rawCode = clean(row[codeColumn] ?? '')
    const description = clean(row[descriptionColumn] ?? '')
    const rawAmount = clean(row[amountColumn] ?? '')

    if (rawCode === '') {
      // No code means this row is not a budget line: a section heading, a
      // subtotal, or spacing. Only an amount with no code *and* no sign of
      // being a total is worth mentioning, since that one might be a line
      // someone forgot to code.
      if (rawAmount !== '' && description !== '' && !/total/i.test(description)) {
        problems.push({
          row: description,
          reason: 'Has an amount but no account code, so it cannot be imported.',
        })
      }
      continue
    }

    const label = description === '' ? rawCode : `${rawCode} — ${description}`
    const accountCode = normalizeAccountCode(rawCode)
    if (accountCode === null) {
      problems.push({
        row: label,
        reason: accountCodeError(rawCode) ?? 'Not a valid account code.',
      })
      continue
    }

    if (seen.has(accountCode)) {
      problems.push({
        row: label,
        reason: 'This account code appears earlier in the file. The first one is used.',
      })
      continue
    }

    if (description === '') {
      problems.push({ row: rawCode, reason: 'Has no description to name the code by.' })
      continue
    }

    const amount = parseAmount(rawAmount)
    if (amount === null) {
      problems.push({ row: label, reason: `"${rawAmount}" is not an amount.` })
      continue
    }
    if (amount < 0) {
      problems.push({ row: label, reason: 'A starting budget cannot be negative.' })
      continue
    }

    seen.add(accountCode)
    const displayName =
      description.length > DISPLAY_NAME_MAX
        ? description.slice(0, DISPLAY_NAME_MAX).trimEnd()
        : description
    const short = suggestShortLabel(displayName)

    lines.push({
      accountCode,
      description: displayName,
      amount,
      suggestedShortLabel: short.label,
      shortLabelShortened: short.shortened,
      category: guessCategory(displayName),
    })
  }

  return {
    lines,
    problems,
    columns: {
      accountCode: headings.code,
      description: headings.description,
      amount: headings.amount,
    },
    titles,
    namedFiscalYear: fiscalYearNamedIn([...titles, headings.amount]),
  }
}

interface FoundHeader {
  rowIndex: number
  codeColumn: number
  descriptionColumn: number
  amountColumn: number
  headings: { code: string; description: string; amount: string }
}

function findHeader(
  grid: readonly (readonly (string | undefined)[])[],
  targetYear?: number,
): FoundHeader | null {
  const limit = Math.min(grid.length, HEADER_SEARCH_ROWS)

  for (let rowIndex = 0; rowIndex < limit; rowIndex += 1) {
    const cells = Array.from(grid[rowIndex] ?? [], (cell) => clean(cell ?? ''))

    const codeColumn = cells.findIndex((text) =>
      /^(account|acct\.?)\s*(code|number|no\.?|#)?$|^(budget\s*)?code$/i.test(text),
    )
    if (codeColumn === -1) continue

    const descriptionColumn = cells.findIndex(
      (text, index) =>
        index !== codeColumn && /^(description|account\s*description|name|title)$/i.test(text),
    )
    if (descriptionColumn === -1) continue

    const amountColumn = bestAmountColumn(cells, [codeColumn, descriptionColumn], targetYear)
    if (amountColumn === -1) continue

    return {
      rowIndex,
      codeColumn,
      descriptionColumn,
      amountColumn,
      headings: {
        code: cells[codeColumn] ?? '',
        description: cells[descriptionColumn] ?? '',
        amount: cells[amountColumn] ?? '',
      },
    }
  }

  return null
}

/**
 * The column holding the starting budget.
 *
 * Scored rather than matched on one word, because a real workbook may carry
 * several money columns — last year's actuals beside this year's proposal — and
 * taking the first "budget" column would import the wrong year's figures while
 * looking right. A notes or comments column is never a candidate.
 *
 * **A year in the heading outweighs its wording**, when the caller says which
 * year is being imported. Wording alone got this wrong: "2025/26 Adopted
 * Budget" scores 4 and "2026/27 Budget" scores 3, so a workbook laid out that
 * way — the ordinary one, last year on the left — imported last year's
 * balances under this year's codes. Every figure looks plausible, which is the
 * failure this scoring exists to prevent. A heading naming the target year is
 * lifted clear of the wording scores; one naming a different year is pushed
 * below everything else.
 */
function bestAmountColumn(
  cells: readonly string[],
  taken: readonly number[],
  targetYear?: number,
): number {
  let best = -1
  let bestScore = 0

  cells.forEach((text, index) => {
    if (taken.includes(index) || text === '') return
    if (/note|comment|memo|reason/i.test(text)) return

    let score = 0
    if (/budget/i.test(text)) score += 3
    if (/amount|allocat|appropriat|balance|starting|beginning|total/i.test(text)) score += 2
    if (/propos|adopt|approv|final|current/i.test(text)) score += 1
    if (/actual|spent|expend|prior|previous|last\s*year|variance|remaining/i.test(text)) score -= 4

    // Gaps of 10 rather than 1, so no amount of wording can bridge them.
    if (targetYear !== undefined && score > 0) {
      const named = fiscalYearNamedIn([text])
      if (named === targetYear) score += 10
      else if (named !== null) score -= 10
    }

    if (score > bestScore) {
      best = index
      bestScore = score
    }
  })

  return best
}

/** "2026-27", "2026/27" or "2026-2027" → 2027, the ending year the app uses. */
export function fiscalYearNamedIn(texts: readonly string[]): number | null {
  for (const text of texts) {
    const match = /\b(20\d{2})\s*[-–/]\s*(\d{4}|\d{2})\b/.exec(text)
    if (match === null) continue
    const start = Number(match[1])
    const tail = match[2] ?? ''
    const end = tail.length === 2 ? Math.floor(start / 100) * 100 + Number(tail) : Number(tail)
    if (end === start + 1) return end
  }
  return null
}

/**
 * Dollars from whatever a spreadsheet cell holds.
 *
 * Handles a plain number, "$1,200.50", and the accountant's "(500)" for a
 * negative. Blank is zero: a budget line with nothing in its amount is a line
 * with no money yet, which is exactly what the $0 lines in the district's file
 * say explicitly. Rounded through cents, since a cell can store 16.07000001.
 */
export function parseAmount(text: string): number | null {
  const trimmed = text.trim()
  if (trimmed === '' || trimmed === '-') return 0

  // A trailing minus is how some accounting exports write a credit — `500-`.
  // Read as a leading one it was invisible, and `500` imported as a positive
  // beginning balance instead of being refused by the caller's `amount < 0`
  // check. The hyphen strip is anchored for the same reason: unanchored, a
  // typed range like `5-10` closed up into 510 rather than being refused.
  const negative =
    /^\(.*\)$/.test(trimmed) || trimmed.startsWith('-') || trimmed.endsWith('-')
  const digits = trimmed.replace(/[()$,\s]/g, '').replace(/^-|-$/g, '')
  if (!/^\d*\.?\d+$/.test(digits)) return null

  const value = toCents(Number(digits)) / 100
  return negative ? -value : value
}

function clean(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

// ─────────────────────────────────────────────────────────────────────────────
// Short labels
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Abbreviations for the words district budget descriptions actually use.
 * Ordered longest phrase first, so "Professional Development" is taken whole
 * before "Professional" alone would be.
 */
const ABBREVIATIONS: readonly [RegExp, string][] = [
  [/\bkindergarten\b/gi, 'K'],
  [/\bfirst grade\b/gi, '1st'],
  [/\bsecond grade\b/gi, '2nd'],
  [/\bthird grade\b/gi, '3rd'],
  [/\bfourth grade\b/gi, '4th'],
  [/\bfifth grade\b/gi, '5th'],
  [/\bsixth grade\b/gi, '6th'],
  [/\bseventh grade\b/gi, '7th'],
  [/\beighth grade\b/gi, '8th'],
  [/\bninth grade\b/gi, '9th'],
  [/\btenth grade\b/gi, '10th'],
  [/\beleventh grade\b/gi, '11th'],
  [/\btwelfth grade\b/gi, '12th'],
  // "7th Grade" once the ordinal has been shortened, so a repeat collapses.
  [/\b(\d+(st|nd|rd|th)) grade\b/gi, '$1'],
  [/\bbuilding[- ]wide\b/gi, 'Bldg'],
  [/\bbuilding\b/gi, 'Bldg'],
  [/\bnon[- ]certified\b/gi, 'Non-Cert.'],
  [/\bprof\.? dev\.?(?=\s|$)/gi, 'Prof. Dev.'],
  [/\bprofessional development\b/gi, 'Prof. Dev.'],
  [/\blanguage arts\b/gi, 'ELA'],
  [/\bsocial studies\b/gi, 'Soc. St.'],
  [/\bworld language\b/gi, 'World Lang.'],
  [/\bdues (and|&) fees\b/gi, 'Dues/Fees'],
  // "of equipment" is what repair and maintenance is *of* — it adds length and
  // no meaning, and keeping it made two different lines both end "Equip.".
  [/\brepairs?\s*(and|&|\/)\s*maint(enance|\.)?(\s+(of\s+)?equip(ment|\.)?)?/gi, 'Repair/Maint.'],
  [/\bmeals (and|&) refreshments\b/gi, 'Meals'],
  [/\breplacement\b/gi, 'Repl.'],
  [/\bequipment\b/gi, 'Equip.'],
  [/\bprofessional\b/gi, 'Prof.'],
  [/\bdevelopment\b/gi, 'Dev.'],
  [/\btechnology\b/gi, 'Tech'],
  [/\bmaintenance\b/gi, 'Maint.'],
  [/\bconference\b/gi, 'Conf.'],
  [/\bsubscriptions?\b/gi, 'Subs.'],
  [/\bservices?\b/gi, 'Serv.'],
  [/\bmaterials\b/gi, 'Materials'],
  [/\band\b/gi, '&'],
]

/** Words that carry nothing once the rest is abbreviated. */
const FILLER = /\b(of|the|for|a|an)\b/gi

/**
 * A short label for a budget description, at most 24 characters.
 *
 * Every description goes through the same steps, even one that already fits,
 * so a list reads consistently — "Principal Travel" beside "Principal Meals",
 * not "Principal - Travel" beside it. Each step loses a little more, and the
 * first result that fits is taken: abbreviated with repeated words removed;
 * then without a parenthetical; then without filler words; then the first part
 * and the last part only; and finally the first part plus as much of the *end*
 * of the rest as fits.
 *
 * The end, not the beginning, because a budget line names its context first and
 * the thing bought last — "Nursing - Health Serv. Repair and Maintenance".
 * Cutting from the end kept "Nursing Health Serv." and threw away the repair,
 * which is the one word that told it apart from the supplies line below it.
 *
 * "Seventh Grade - Science Supplies" → "7th Science Supplies".
 *
 * It is a *suggestion*. The preview shows it editable, marks it when it differs
 * from the description, and flags two lines that ended up with the same label,
 * because no rule reads a description as well as the person who wrote it.
 */
export function suggestShortLabel(description: string): { label: string; shortened: boolean } {
  const original = clean(description)
  const label = shortLabelFor(original)
  return { label, shortened: label !== original }
}

function shortLabelFor(original: string): string {
  const parts = original
    .split(/\s+[-–—]+\s*|\s*[-–—]+\s+|-{2,}/)
    .map((part) => abbreviate(part))
    .map((part) => tidy(part))
    .filter((part) => part !== '')

  const withoutParens = (text: string) => tidy(text.replace(/\([^)]*\)/g, ''))
  const withoutFiller = (text: string) => tidy(text.replace(FILLER, ''))

  const joined = tidy(dedupeWords(parts.join(' ')))
  const candidates = [joined, withoutParens(joined), withoutFiller(withoutParens(joined))]

  if (parts.length >= 2) {
    const first = withoutParens(parts[0]!)
    const last = withoutFiller(withoutParens(parts[parts.length - 1]!))
    candidates.push(tidy(dedupeWords(`${first} ${last}`)))
  }

  for (const candidate of candidates) {
    if (candidate.length > 0 && candidate.length <= SHORT_LABEL_MAX) return candidate
  }

  return keepContextAndEnd(parts.map((part) => withoutFiller(withoutParens(part))))
}

/**
 * The first part, then as many of the *last* words of the rest as still fit.
 * Falls back to cutting at a word when even the first part is too long.
 */
function keepContextAndEnd(parts: readonly string[]): string {
  const context = parts[0] ?? ''
  const restWords = dedupeWords(`${context} ${parts.slice(1).join(' ')}`)
    .split(' ')
    .slice(context.split(' ').length)

  if (parts.length < 2 || context.length >= SHORT_LABEL_MAX - 2 || restWords.length === 0) {
    return cutAtWord(tidy(dedupeWords(parts.join(' '))))
  }

  for (let from = 0; from < restWords.length; from += 1) {
    // Never start the kept end on a joining word — "Principal & Refreshments".
    if (/^([-–—/&]|and|or)$/i.test(restWords[from] ?? '')) continue
    const candidate = tidy(`${context} ${restWords.slice(from).join(' ')}`)
    if (candidate.length <= SHORT_LABEL_MAX) return candidate
  }
  return cutAtWord(context)
}

function abbreviate(text: string): string {
  let result = text
  for (const [pattern, replacement] of ABBREVIATIONS) {
    result = result.replace(pattern, replacement)
  }
  return result
}

/** Collapse spacing and drop stray joining punctuation left at either end. */
function tidy(text: string): string {
  return text
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s*([-–—/&])\s*$/, '')
    .replace(/^\s*[-–—/&]\s*/, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** "Principal Office Principal Fees" → "Principal Office Fees". */
function dedupeWords(text: string): string {
  const seen = new Set<string>()
  return text
    .split(' ')
    .filter((word) => {
      // Compared by stem, so "Principal" and "Principals'" are one word.
      const key = word.toLowerCase().replace(/[^a-z0-9]/g, '').replace(/s$/, '')
      if (key === '') return true
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .join(' ')
}

function cutAtWord(text: string): string {
  if (text.length <= SHORT_LABEL_MAX) return text
  const words = text.split(' ')
  let out = ''
  for (const word of words) {
    const next = out === '' ? word : `${out} ${word}`
    if (next.length > SHORT_LABEL_MAX) break
    out = next
  }
  // A single word longer than the limit still needs *a* label.
  return tidy(out === '' ? text.slice(0, SHORT_LABEL_MAX) : out)
}

// ─────────────────────────────────────────────────────────────────────────────
// Category
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A category from the description's wording, defaulting to "other".
 *
 * Deliberately from words rather than from the object code. The object code
 * would be the principled source, but the same code carries different things
 * in different buildings' files — 360 is "Prof. Dev. Fees" on one budget and
 * "Repairs & Maint. of Equip." on another — and category is for reference only,
 * so a wrong confident guess is worse than an honest "other".
 */
export function guessCategory(description: string): Category {
  const text = description.toLowerCase()
  if (/\btravel\b|\bmileage\b|\blodging\b|\bairfare\b/.test(text)) return 'travel'
  if (/\bconference\b|\btraining\b|\bworkshop\b|\bprof(essional)?\.? ?dev/.test(text)) {
    return 'conference'
  }
  if (/\bmeals?\b|\brefreshments?\b|\bfood\b|\bcatering\b/.test(text)) return 'meals'
  if (/\btech(nology)?\b|\bsoftware\b|\bonline\b|\blicen[cs]es?\b|\bcomputer/.test(text)) {
    return 'tech'
  }
  if (/\brepairs?\b|\bmaint|\bprinting\b|\bservices?\b|\balert system\b|\bphones?\b/.test(text)) {
    return 'services'
  }
  return 'other'
}

// ─────────────────────────────────────────────────────────────────────────────
// Against what is already set up
// ─────────────────────────────────────────────────────────────────────────────

export interface ExistingCodeForImport {
  id: string
  code: string
  beginningBalance: number
  external: boolean
}

export type PlannedImportLine = BudgetImportLine &
  (
    | { status: 'new' }
    | { status: 'exists'; existingId: string; existingBalance: number }
    /** The code is already set up as someone else's (external) code. */
    | { status: 'external'; existingId: string }
  )

/**
 * Mark each line as new, already set up, or clashing with an external code —
 * matched on the canonical account code within the chosen fiscal year.
 *
 * A clash with an external code is never updated from here: that document is a
 * colleague's code filed against by charges, and a budget import has no
 * business turning it into one of the importer's own.
 */
export function planBudgetImport(
  lines: readonly BudgetImportLine[],
  existing: readonly ExistingCodeForImport[],
): PlannedImportLine[] {
  const byCode = new Map<string, ExistingCodeForImport>()
  for (const code of existing) {
    const canonical = normalizeAccountCode(code.code)
    if (canonical !== null) byCode.set(canonical, code)
  }

  return lines.map((line) => {
    const match = byCode.get(line.accountCode)
    if (match === undefined) return { ...line, status: 'new' }
    if (match.external) return { ...line, status: 'external', existingId: match.id }
    return {
      ...line,
      status: 'exists',
      existingId: match.id,
      existingBalance: match.beginningBalance,
    }
  })
}
